/**
 * Analytics helper — thin wrapper around window.dataLayer so the rest of the
 * app never touches GTM directly.
 *
 * Usage from anywhere:
 *   import { trackEvent } from "../lib/analytics";
 *   trackEvent("search_submit", { search_type: "name", query: "smith" });
 *
 * Safe during SSR / when GTM is blocked — always guards on window.
 */

const safePush = (payload) => {
  try {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch {
    /* silent — analytics must never break the app */
  }
};

/**
 * Fire a GA4/GTM page_view event. Called from GTMPageViewTracker on every
 * React Router navigation. Page title is read fresh from the DOM so
 * Helmet-injected titles are captured accurately.
 */
export const trackPageView = (pathname) => {
  safePush({
    event: "page_view",
    page_path: pathname,
    page_location: typeof window !== "undefined" ? window.location.href : "",
    page_title: typeof document !== "undefined" ? document.title : "",
  });
};

/**
 * Fire a custom GTM event. Map to a "Google Analytics: GA4 Event" tag in
 * GTM with trigger = Custom Event matching the `event` name.
 *
 * Standard events we fire across the app:
 *   - search_submit   → {search_type, query}
 *   - provider_view   → {npi, specialty, state, entity_type}
 *   - bulk_screening  → {source, npi_count}
 *   - reconcile_run   → {source, roster_size}
 *   - signup / login  → {method}
 */
export const trackEvent = (name, params = {}) => {
  safePush({ event: name, ...params });
};
